import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle, RefreshCw, Search, Users } from 'lucide-react';

function StatusBadge({ status }) {
  const variants = {
    OVER_UTILISED: 'bg-red-500/10 text-red-600 border-red-500/20',
    NORMAL: 'bg-green-500/10 text-green-600 border-green-500/20',
    UNDER_UTILISED: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  };
  return (
    <Badge variant="outline" className={variants[status] || 'bg-muted'}>
      {status?.replace(/_/g, ' ')}
    </Badge>
  );
}

function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default' }) {
  const variants = {
    default: 'bg-card',
    danger: 'bg-red-500/5 border-red-500/20',
    warning: 'bg-amber-500/5 border-amber-500/20',
    success: 'bg-green-500/5 border-green-500/20',
  };
  return (
    <Card className={variants[variant]} data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          {Icon && (
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
              variant === 'danger' ? 'bg-red-500/10' :
              variant === 'warning' ? 'bg-amber-500/10' :
              variant === 'success' ? 'bg-green-500/10' : 'bg-primary/10'
            }`}>
              <Icon className={`h-5 w-5 ${
                variant === 'danger' ? 'text-red-500' :
                variant === 'warning' ? 'text-amber-500' :
                variant === 'success' ? 'text-green-500' : 'text-primary'
              }`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function FundingForecastPage() {
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [recalculating, setRecalculating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [forecastRes, summaryRes] = await Promise.all([
        api.get('/forecast/participants'),
        api.get('/forecast/summary')
      ]);
      setForecasts(forecastRes.data.items || []);
      setSummary(summaryRes.data.data || {});
    } catch (err) {
      toast.error('Failed to load forecasts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await api.post('/forecast/recalculate', {});
      toast.success('Forecasts recalculated');
      fetchData();
    } catch (err) {
      toast.error('Failed to recalculate');
    } finally {
      setRecalculating(false);
    }
  };

  const filtered = forecasts.filter(f => {
    const matchSearch = !search || 
      f.participantName?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || f.forecastStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  const formatCurrency = (val) => {
    if (val === undefined || val === null) return '$0';
    return `$${Number(val).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="space-y-6" data-testid="funding-forecast-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Funding Forecast Dashboard</h1>
          <p className="text-muted-foreground">Participant funding utilisation and projections</p>
        </div>
        <Button onClick={handleRecalculate} disabled={recalculating} data-testid="recalculate-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
          Recalculate All
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          title="Total Participants"
          value={summary?.totalForecasts || 0}
          icon={Users}
          variant="default"
        />
        <KpiCard
          title="Over-Utilised"
          value={summary?.overUtilisedCount || 0}
          subtitle="Above expected usage"
          icon={TrendingUp}
          variant="danger"
        />
        <KpiCard
          title="Normal"
          value={summary?.normalCount || 0}
          subtitle="On track"
          icon={DollarSign}
          variant="success"
        />
        <KpiCard
          title="Under-Utilised"
          value={summary?.underUtilisedCount || 0}
          subtitle="Below expected usage"
          icon={TrendingDown}
          variant="warning"
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search participants..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="OVER_UTILISED">Over-Utilised</SelectItem>
                <SelectItem value="NORMAL">Normal</SelectItem>
                <SelectItem value="UNDER_UTILISED">Under-Utilised</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Funding Forecasts</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Participant</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Utilisation</TableHead>
                  <TableHead>Forecast Exhaustion</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No forecasts found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((f) => (
                    <TableRow key={f.id} data-testid={`forecast-row-${f.participantId}`}>
                      <TableCell className="font-medium">{f.participantName || 'Unknown'}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(f.totalBudget)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(f.usedBudget)}</TableCell>
                      <TableCell className="text-right">
                        <span className={`font-medium ${
                          f.utilisationPercent > 100 ? 'text-red-600' :
                          f.utilisationPercent < 50 ? 'text-amber-600' : 'text-green-600'
                        }`}>
                          {f.utilisationPercent?.toFixed(1) || 0}%
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {f.projectedExhaustionDate || '-'}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={f.forecastStatus} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
